import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { createMedico } from "../services/medicoService"; // Importa el servicio para guardar médicos
import { getSedes } from "../services/sedeService"; // Importa el servicio para obtener sedes

export default function CreateCurriculum() {
  const [profileImage, setProfileImage] = useState(null);
  const [sedes, setSedes] = useState([]); // Estado para las sedes
  const [jsonInput, setJsonInput] = useState(""); // Estado para el JSON

  const [formData, setFormData] = useState({
    nombreCompleto: "",
    nacionalidad: "",
    fechaNacimiento: "",
    email: "",
    telefono: "",
    direccion: "",
    sede: "", // Campo para la sede seleccionada
    aplicaEnTodasLasSedes: false, // Checkbox por defecto en false
  });

  const [academicInfo, setAcademicInfo] = useState([
    { gradoAcademico: "", institucion: "", anoGraduacion: "" },
  ]);

  const [professionalExperience, setProfessionalExperience] = useState([
    { posicion: "", institucionTrabajo: "", fechaInicio: "", fechaFin: "", descripcionExperiencia: "" },
  ]);

  const [certifications, setCertifications] = useState([
    { nombreCertificacion: "", institucionCertificacion: "", anoCertificacion: "" },
  ]);

  const [publications, setPublications] = useState([
    { tituloPublicacion: "", fechaPublicacion: "", descripcionPublicacion: "" },
  ]);

  const [awards, setAwards] = useState([
    { nombrePremio: "", anoPremio: "", descripcionPremio: "" },
  ]);

  // Obtener las sedes de Firebase
  useEffect(() => {
    const fetchSedes = async () => {
      try {
        const sedesData = await getSedes(); // Obtiene las sedes desde Firebase
        setSedes(sedesData);
      } catch (error) {
        console.error("Error obteniendo las sedes:", error);
      }
    };

    fetchSedes();
  }, []);

  // Manejar la carga de imagen
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file); // Guardamos el archivo real, no Base64
    }
  };

  // Manejar los cambios en los campos de información personal
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Manejar el checkbox de aplica en todas las sedes
  const handleCheckboxChange = (e) => {
    setFormData({
      ...formData,
      aplicaEnTodasLasSedes: e.target.checked,
    });
  };

  // Manejar los cambios en los arrays de secciones
  const handleArrayChange = (index, field, value, setState, state) => {
    const newArray = [...state];
    newArray[index][field] = value;
    setState(newArray);
  };

  // Añadir una nueva entrada a las secciones dinámicas
  const handleAddEntry = (setState, state) => {
    setState([...state, {}]);
  };

  // Eliminar una entrada en las secciones dinámicas
  const handleRemoveEntry = (index, setState, state) => {
    const newArray = [...state];
    newArray.splice(index, 1);
    setState(newArray);
  };

// Manejar el envío del formulario
const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    // **Reemplazar fechaFin vacía con "Actual" antes de enviar**
    const professionalExperienceWithActual = professionalExperience.map(experience => ({
      ...experience,
      fechaFin: experience.fechaFin || "Actual" 
    }));

    const medicoData = {
      ...formData,
      academicInfo,
      professionalExperience: professionalExperienceWithActual, // Usar la nueva variable
      certifications,
      publications,
      awards,
    };

    const id = await createMedico(medicoData, profileImage); 
    console.log("ID del usuario almacenado:", id); 
    alert("Currículum guardado exitosamente");
  } catch (error) {
    console.error("Error guardando currículum:", error);
    alert("Ocurrió un error al guardar el currículum");
  }
};

// Manejar el envío del JSON pegado
const handleJsonSubmit = async () => {
  try {
    const parsedJson = JSON.parse(jsonInput); // Parsear el JSON
    const id = await createMedico(parsedJson, profileImage); // Guardar el JSON directamente y obtener el ID
    console.log("ID del usuario almacenado desde JSON:", id); // Muestra el ID en la consola
    alert("Currículum guardado exitosamente desde JSON");
  } catch (error) {
    console.error("Error guardando currículum desde JSON:", error);
    alert("Ocurrió un error al guardar el currículum desde JSON");
  }
};

  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom>
              Crear Perfil Médico Web
            </Typography>
      <form onSubmit={handleSubmit}>
        {/* Cabecera con la imagen de perfil */}
        <Paper sx={{ marginBottom: 4, padding: 3 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "16px" }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              Cargar Foto de Perfil
            </Typography>
            <Avatar
              alt="Perfil"
              src={profileImage ? URL.createObjectURL(profileImage) : ""}
              sx={{ width: 100, height: 100, margin: "auto" }}
            />
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ marginTop: "16px" }}
            />
          </div>
        </Paper>

        {/* Información Personal */}
        <Paper sx={{ marginBottom: 4, padding: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Información Personal
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre Completo"
                variant="outlined"
                fullWidth
                name="nombreCompleto"
                value={formData.nombreCompleto}
                onChange={handleChange}
                sx={{ marginBottom: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nacionalidad"
                variant="outlined"
                fullWidth
                name="nacionalidad"
                value={formData.nacionalidad}
                onChange={handleChange}
                sx={{ marginBottom: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de Nacimiento"
                variant="outlined"
                fullWidth
                type="date"
                name="fechaNacimiento"
                value={formData.fechaNacimiento}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{ marginBottom: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleChange}
                sx={{ marginBottom: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                variant="outlined"
                fullWidth
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                sx={{ marginBottom: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Dirección"
                variant="outlined"
                fullWidth
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                sx={{ marginBottom: 2 }}
              />
            </Grid>

            {/* Campo de Selección de Sede */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ marginBottom: 2 }} disabled={formData.aplicaEnTodasLasSedes}>
                <InputLabel>Sede</InputLabel>
                <Select
                  name="sede"
                  value={formData.sede}
                  onChange={handleChange}
                  label="Sede"
                >
                  {sedes.map((sede, index) => (
                    <MenuItem key={index} value={sede.nombre}>
                      {sede.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Checkbox para "Aplica en todas las sedes" */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.aplicaEnTodasLasSedes}
                    onChange={handleCheckboxChange}
                    color="primary"
                  />
                }
                label="Aplica en todas las sedes"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Información Académica */}
        <Paper sx={{ marginBottom: 4, padding: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Información Académica
          </Typography>
          {academicInfo.map((info, index) => (
            <Grid container spacing={2} key={index}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Grado Académico"
                  variant="outlined"
                  fullWidth
                  value={info.gradoAcademico}
                  onChange={(e) => handleArrayChange(index, "gradoAcademico", e.target.value, setAcademicInfo, academicInfo)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Institución"
                  variant="outlined"
                  fullWidth
                  value={info.institucion}
                  onChange={(e) => handleArrayChange(index, "institucion", e.target.value, setAcademicInfo, academicInfo)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Año de Graduación"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={info.anoGraduacion}
                  onChange={(e) => handleArrayChange(index, "anoGraduacion", e.target.value, setAcademicInfo, academicInfo)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <IconButton onClick={() => handleRemoveEntry(index, setAcademicInfo, academicInfo)} sx={{ color: "red" }}>
                  <RemoveIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button onClick={() => handleAddEntry(setAcademicInfo, academicInfo)} variant="contained" color="primary" startIcon={<AddIcon />}>
            Agregar Más
          </Button>
        </Paper>

        {/* Experiencia Profesional */}
        <Paper sx={{ marginBottom: 4, padding: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Experiencia Profesional
          </Typography>
          {professionalExperience.map((experience, index) => (
            <Grid container spacing={2} key={index}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Posición"
                  variant="outlined"
                  fullWidth
                  value={experience.posicion}
                  onChange={(e) => handleArrayChange(index, "posicion", e.target.value, setProfessionalExperience, professionalExperience)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Institución"
                  variant="outlined"
                  fullWidth
                  value={experience.institucionTrabajo}
                  onChange={(e) => handleArrayChange(index, "institucionTrabajo", e.target.value, setProfessionalExperience, professionalExperience)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fecha de Inicio"
                  variant="outlined"
                  fullWidth
                  type="date"
                  value={experience.fechaInicio}
                  onChange={(e) => handleArrayChange(index, "fechaInicio", e.target.value, setProfessionalExperience, professionalExperience)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
              <TextField
  label="Fecha de Fin"
  variant="outlined"
  fullWidth
  type="date"
  value={experience.fechaFin}
  onChange={(e) => handleArrayChange(index, "fechaFin", e.target.value, setProfessionalExperience, professionalExperience)}
  InputLabelProps={{ shrink: true }}
  sx={{ marginBottom: 2 }}
  helperText="Dejar en blanco si es tu trabajo actual" // Agrega esta línea
/>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descripción"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={experience.descripcionExperiencia}
                  onChange={(e) => handleArrayChange(index, "descripcionExperiencia", e.target.value, setProfessionalExperience, professionalExperience)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <IconButton onClick={() => handleRemoveEntry(index, setProfessionalExperience, professionalExperience)} sx={{ color: "red" }}>
                  <RemoveIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button onClick={() => handleAddEntry(setProfessionalExperience, professionalExperience)} variant="contained" color="primary" startIcon={<AddIcon />}>
            Agregar Más
          </Button>
        </Paper>

        {/* Certificaciones */}
        <Paper sx={{ marginBottom: 4, padding: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Certificaciones y Licencias
          </Typography>
          {certifications.map((cert, index) => (
            <Grid container spacing={2} key={index}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre de la Certificación"
                  variant="outlined"
                  fullWidth
                  value={cert.nombreCertificacion}
                  onChange={(e) => handleArrayChange(index, "nombreCertificacion", e.target.value, setCertifications, certifications)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Institución"
                  variant="outlined"
                  fullWidth
                  value={cert.institucionCertificacion}
                  onChange={(e) => handleArrayChange(index, "institucionCertificacion", e.target.value, setCertifications, certifications)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Año"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={cert.anoCertificacion}
                  onChange={(e) => handleArrayChange(index, "anoCertificacion", e.target.value, setCertifications, certifications)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <IconButton onClick={() => handleRemoveEntry(index, setCertifications, certifications)} sx={{ color: "red" }}>
                  <RemoveIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button onClick={() => handleAddEntry(setCertifications, certifications)} variant="contained" color="primary" startIcon={<AddIcon />}>
            Agregar Más
          </Button>
        </Paper>

        {/* Publicaciones */}
        <Paper sx={{ marginBottom: 4, padding: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Publicaciones e Investigación
          </Typography>
          {publications.map((pub, index) => (
            <Grid container spacing={2} key={index}>
              <Grid item xs={12}>
                <TextField
                  label="Título de la Publicación"
                  variant="outlined"
                  fullWidth
                  value={pub.tituloPublicacion}
                  onChange={(e) => handleArrayChange(index, "tituloPublicacion", e.target.value, setPublications, publications)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Fecha"
                  variant="outlined"
                  fullWidth
                  type="date"
                  value={pub.fechaPublicacion}
                  onChange={(e) => handleArrayChange(index, "fechaPublicacion", e.target.value, setPublications, publications)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descripción"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={pub.descripcionPublicacion}
                  onChange={(e) => handleArrayChange(index, "descripcionPublicacion", e.target.value, setPublications, publications)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <IconButton onClick={() => handleRemoveEntry(index, setPublications, publications)} sx={{ color: "red" }}>
                  <RemoveIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button onClick={() => handleAddEntry(setPublications, publications)} variant="contained" color="primary" startIcon={<AddIcon />}>
            Agregar Más
          </Button>
        </Paper>

        {/* Premios */}
        <Paper sx={{ marginBottom: 4, padding: 3 }}>
          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Premios y Reconocimientos
          </Typography>
          {awards.map((award, index) => (
            <Grid container spacing={2} key={index}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre del Premio"
                  variant="outlined"
                  fullWidth
                  value={award.nombrePremio}
                  onChange={(e) => handleArrayChange(index, "nombrePremio", e.target.value, setAwards, awards)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Año"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={award.anoPremio}
                  onChange={(e) => handleArrayChange(index, "anoPremio", e.target.value, setAwards, awards)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descripción"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={award.descripcionPremio}
                  onChange={(e) => handleArrayChange(index, "descripcionPremio", e.target.value, setAwards, awards)}
                  sx={{ marginBottom: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <IconButton onClick={() => handleRemoveEntry(index, setAwards, awards)} sx={{ color: "red" }}>
                  <RemoveIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button onClick={() => handleAddEntry(setAwards, awards)} variant="contained" color="primary" startIcon={<AddIcon />}>
            Agregar Más
          </Button>
        </Paper>

        {/* Botón de Enviar */}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ marginTop: 3 }}
        >
          Enviar Curriculum
        </Button>
      </form>

      {/* Apartado para ingresar JSON */}
      {/* <Paper sx={{ marginTop: 4, padding: 3 }}>
        <Typography variant="h5" sx={{ marginBottom: 2 }}>
          Pegar JSON
        </Typography>
        <TextField
          label="Pega tu JSON aquí"
          variant="outlined"
          fullWidth
          multiline
          rows={6}
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
        />
        <Button
          onClick={handleJsonSubmit}
          variant="contained"
          color="secondary"
          sx={{ marginTop: 2 }}
        >
          Enviar JSON
        </Button>
      </Paper> */}
    </Container>
  );
}
